import {
    addUtcYears,
    formatAsMonthDayYearUTC,
    resolveNextAnniversary,
    yearOffset,
} from '@fruitful/fruitils';
import type {
    GuidanceSession,
    GuidanceSessionType,
    SubscriptionProduct,
} from '@fruitful/types';
import { isAfter, isValid, parseISO } from 'date-fns';

import { isVisibleGuidanceSessionStatus } from './GuidanceSessions';

/** Guard for walking a stale stored anniversary back onto the current year. */
export const MAX_MEMBERSHIP_YEARS = 20;

/**
 * `undefined` means the product has no general session of this type.
 * `null` means that session is included with no annual cap.
 */
export const getSessionTypeAnnualLimit = (
    product: SubscriptionProduct | null | undefined,
    sessionType: GuidanceSessionType,
): number | null | undefined => {
    const generalSession = product?.guidance.generalSessions.find(
        (session) => session.guidanceSessionType === sessionType,
    );
    if (generalSession == null) {
        return undefined;
    }
    return generalSession.annualLimit;
};

/**
 * `undefined` means the product has no general session of this type ("none").
 * `null` means that session is included with no annual cap ("unlimited").
 */
export const formatSessionAllowanceLabel = (
    annualLimit: number | null | undefined,
): string => {
    if (annualLimit === undefined) {
        return 'none';
    }
    if (annualLimit === null) {
        return 'unlimited';
    }
    return String(annualLimit);
};

const previousUtcDay = (date: Date): Date => {
    const previous = new Date(date.getTime());
    previous.setUTCDate(previous.getUTCDate() - 1);
    return previous;
};

/**
 * Inclusive range for the year that ends at `anniversary`, shifted by
 * `offset`. Offset `0` is `[anniversary - 1 year, anniversary)`. The displayed
 * end is the day before that exclusive anniversary.
 */
export const formatOffsetYearRange = (
    anniversary: Date,
    offset: number,
): string => {
    const exclusiveEnd = addUtcYears(anniversary, offset);
    const start = addUtcYears(anniversary, offset - 1);
    const endInclusive = previousUtcDay(exclusiveEnd);
    return `${formatAsMonthDayYearUTC(start)} - ${formatAsMonthDayYearUTC(endInclusive)}`;
};

const yearOffsetForSession = (
    yearEnd: Date,
    startTime: string,
    fallbackOffset: number,
): number => {
    const start = parseISO(startTime);
    return isValid(start)
        ? yearOffset(yearEnd, start, MAX_MEMBERSHIP_YEARS)
        : fallbackOffset;
};

const isAfterEpoch = (startTime: string): boolean =>
    new Date(startTime).getTime() > 0;

/**
 * Sessions of `sessionType` in the current membership year that have already
 * started and whose stored status counts. A start time in the future does not
 * occupy a slot. Sessions with a start time of 1970-01-01 are skipped
 * placeholders.
 */
export const sessionsCountingTowardAnnualLimit = (
    sessions: readonly GuidanceSession[],
    sessionType: GuidanceSessionType,
    membershipYearEndsAt: Date,
): GuidanceSession[] => {
    if (!isValid(membershipYearEndsAt)) {
        return [];
    }

    const now = new Date();
    const yearEnd = resolveNextAnniversary(
        membershipYearEndsAt,
        now,
        MAX_MEMBERSHIP_YEARS,
    );
    return sessions.filter((session) => {
        if (session.type !== sessionType) {
            return false;
        }
        if (!isAfterEpoch(session.startTime)) {
            return false;
        }
        if (isAfter(parseISO(session.startTime), now)) {
            return false;
        }
        if (!isVisibleGuidanceSessionStatus(session.status)) {
            return false;
        }
        return yearOffsetForSession(yearEnd, session.startTime, 0) === 0;
    });
};

export const countSessionsTowardAnnualLimit = (
    sessions: readonly GuidanceSession[],
    sessionType: GuidanceSessionType,
    membershipYearEndsAt: Date,
): number =>
    sessionsCountingTowardAnnualLimit(
        sessions,
        sessionType,
        membershipYearEndsAt,
    ).length;

export interface MembershipYearGroup {
    yearOffset: number;
    rangeLabel: string;
    isCurrentYear: boolean;
    /** Oldest start time first. */
    sessions: GuidanceSession[];
}

interface MembershipYearSessionsView {
    currentYearRangeLabel: string | null;
    /**
     * Current membership year, plus any later year, oldest first.
     * Later years stay visible so a session booked past the anniversary
     * is not dropped from the guidance page.
     */
    timelineSessions: GuidanceSession[];
    hasEarlierSessions: boolean;
    /** Newest year first. Empty years are omitted. */
    years: MembershipYearGroup[];
}

const compareStartAscending = (
    a: GuidanceSession,
    b: GuidanceSession,
): number => parseISO(a.startTime).getTime() - parseISO(b.startTime).getTime();

const unlabeledYearView = (
    sorted: GuidanceSession[],
): MembershipYearSessionsView => ({
    currentYearRangeLabel: null,
    timelineSessions: sorted,
    hasEarlierSessions: false,
    years:
        sorted.length === 0
            ? []
            : [
                  {
                      yearOffset: 0,
                      rangeLabel: '',
                      isCurrentYear: true,
                      sessions: sorted,
                  },
              ],
});

export const buildMembershipYearSessionsView = ({
    membershipYearEndsAt,
    now,
    sessions,
}: {
    membershipYearEndsAt: Date;
    now: Date;
    sessions: readonly GuidanceSession[];
}): MembershipYearSessionsView => {
    const sorted = sessions
        .filter((session) => isAfterEpoch(session.startTime))
        .slice()
        .sort(compareStartAscending);

    if (!isValid(membershipYearEndsAt)) {
        return unlabeledYearView(sorted);
    }

    const yearEnd = resolveNextAnniversary(
        membershipYearEndsAt,
        now,
        MAX_MEMBERSHIP_YEARS,
    );
    const byYear = new Map<number, GuidanceSession[]>();
    for (const session of sorted) {
        const offset = yearOffsetForSession(yearEnd, session.startTime, 0);
        const existing = byYear.get(offset);
        if (existing != null) {
            existing.push(session);
        } else {
            byYear.set(offset, [session]);
        }
    }

    const years = [...byYear.keys()]
        .sort((a, b) => b - a)
        .map((offset) => ({
            yearOffset: offset,
            rangeLabel: formatOffsetYearRange(yearEnd, offset),
            isCurrentYear: offset === 0,
            sessions: byYear.get(offset) ?? [],
        }));

    return {
        currentYearRangeLabel: formatOffsetYearRange(yearEnd, 0),
        timelineSessions: years
            .filter((year) => year.yearOffset >= 0)
            .flatMap((year) => year.sessions)
            .sort(compareStartAscending),
        hasEarlierSessions: years.some((year) => year.yearOffset < 0),
        years,
    };
};
